/* ==========================================================================
   EKLIPSEE — progressive enhancement only.
   Nothing here is required for the page to be readable, purchasable or
   accessible; if it never runs, the site still sells.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Scroll reveal
     Elements start hidden only once JS confirms it can reveal them, so a
     failed script never leaves content invisible.
     ---------------------------------------------------------------------- */

  /* Stagger tuning.

     STEP is small and TOTAL is capped on purpose. An uncapped stagger turns a
     12-item grid into a card trick the visitor has to sit through; capped, the
     cascade is felt without ever being waited on. */
  var STAGGER_STEP = 60;
  var STAGGER_MAX = 400;

  /**
   * Groups are detected rather than declared: any element with two or more
   * direct `.ek-reveal` children is a cascade. That keeps the markup clean and
   * means a section written next month staggers correctly without anyone
   * remembering to add an attribute. `[data-ek-stagger]` stays supported for
   * the case where a group needs to be forced.
   */
  function applyStagger() {
    var groups = new Set();

    document.querySelectorAll('.ek-reveal').forEach(function (el) {
      if (el.parentElement) groups.add(el.parentElement);
    });

    document.querySelectorAll('[data-ek-stagger]').forEach(function (el) {
      groups.add(el);
    });

    groups.forEach(function (group) {
      var items = [];

      for (var i = 0; i < group.children.length; i++) {
        if (group.children[i].classList.contains('ek-reveal')) {
          items.push(group.children[i]);
        }
      }

      if (items.length < 2) return;

      items.forEach(function (item, index) {
        // Don't override a delay the markup set on purpose (--d1/--d2/--d3).
        if (
          item.classList.contains('ek-reveal--d1') ||
          item.classList.contains('ek-reveal--d2') ||
          item.classList.contains('ek-reveal--d3')
        ) {
          return;
        }

        item.style.setProperty(
          '--ek-stagger',
          Math.min(index * STAGGER_STEP, STAGGER_MAX) + 'ms'
        );
      });
    });
  }

  function initReveal() {
    // `.ek-corona` is observed directly as well as inheriting from an ancestor,
    // so a marker that sits outside a revealed block still performs its transit.
    var targets = document.querySelectorAll('.ek-reveal, .ek-reveal-wipe, .ek-corona');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    applyStagger();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Order-by countdown

     Markup contract:
       <span class="ek-countdown" data-ek-countdown="2026-08-05T23:59:00+02:00"
             data-label-days="days" data-label-hours="hrs" data-label-mins="min"
             data-expired="Ordering has closed for this eclipse."></span>

     The server-rendered fallback text stays in place until this runs, so
     crawlers and no-JS visitors still see the deadline.
     ---------------------------------------------------------------------- */

  function initCountdown() {
    var nodes = document.querySelectorAll('[data-ek-countdown]');
    if (!nodes.length) return;

    var clocks = [];

    nodes.forEach(function (node) {
      var target = Date.parse(node.getAttribute('data-ek-countdown'));
      if (isNaN(target)) return;
      clocks.push({
        node: node,
        target: target,
        /* Seconds are opt-in: a clock counting down whole days does not need
           them, and ticking every second would repaint the page 30x more
           often for no information gained. */
        seconds: node.getAttribute('data-ek-countdown-seconds') === 'true',
        /* Set when the surrounding markup makes a claim that stops being true
           at zero ("last hour to order"). Swapping the clock text is not
           enough there — the whole block has to be re-rendered by Liquid. */
        reload: node.getAttribute('data-ek-countdown-reload') === 'true',
        wasLive: false,
      });
    });

    if (!clocks.length) return;

    function unit(value, label) {
      return (
        '<span>' +
        value +
        '</span><span class="ek-countdown__unit">' +
        label +
        '</span>'
      );
    }

    function tick() {
      var now = Date.now();
      var live = false;
      var wantsSeconds = false;

      clocks.forEach(function (clock) {
        var remaining = clock.target - now;

        if (remaining <= 0) {
          var expired = clock.node.getAttribute('data-expired');
          clock.node.textContent = expired || '';
          if (!expired) clock.node.classList.add('ek-hide');
          /* Only reload if this page was showing a running clock: a page that
             loaded already-expired was rendered in its expired state by
             Liquid, and reloading it would loop. */
          if (clock.reload && clock.wasLive) {
            clock.reload = false;
            window.location.reload();
          }
          return;
        }

        live = true;
        clock.wasLive = true;
        if (clock.seconds) wantsSeconds = true;

        var totalSeconds = Math.floor(remaining / 1000);
        var days = Math.floor(totalSeconds / 86400);
        var hours = Math.floor((totalSeconds % 86400) / 3600);
        var minutes = Math.floor((totalSeconds % 3600) / 60);

        var html = '';
        if (days > 0) {
          html += unit(days, clock.node.getAttribute('data-label-days') || 'd');
        }
        html += unit(hours, clock.node.getAttribute('data-label-hours') || 'h');
        html += unit(minutes, clock.node.getAttribute('data-label-mins') || 'm');
        if (clock.seconds) {
          html += unit(
            totalSeconds % 60,
            clock.node.getAttribute('data-label-secs') || 's'
          );
        }

        clock.node.innerHTML = html;
      });

      if (live) window.setTimeout(tick, wantsSeconds ? 1000 : 30000);
    }

    tick();
  }

  /* ------------------------------------------------------------------------
     Colour swatches on cards
     Swapping the card image on hover/focus lets the range be browsed from the
     home page, without a page load per colour.
     ---------------------------------------------------------------------- */

  function initCardSwatches() {
    document.querySelectorAll('[data-ek-swatch-group]').forEach(function (group) {
      var card = group.closest('[data-ek-card]');
      if (!card) return;

      var image = card.querySelector('[data-ek-card-image]');
      var link = card.querySelector('[data-ek-card-link]');
      if (!image) return;

      var initialSrc = image.getAttribute('src');
      var initialSrcset = image.getAttribute('srcset');
      var initialHref = link ? link.getAttribute('href') : null;

      function apply(button) {
        var src = button.getAttribute('data-ek-swatch-image');
        var srcset = button.getAttribute('data-ek-swatch-srcset');
        var href = button.getAttribute('data-ek-swatch-url');

        if (src) image.setAttribute('src', src);
        if (srcset) image.setAttribute('srcset', srcset);
        else if (src) image.removeAttribute('srcset');
        if (href && link) link.setAttribute('href', href);

        // Swatches render as <a> (they degrade to a real variant link without
        // JS). aria-pressed is only valid on buttons, so links get aria-current
        // instead — same meaning to assistive tech, valid ARIA either way.
        group.querySelectorAll('.ek-swatch').forEach(function (el) {
          var isActive = el === button;
          var attr = el.tagName === 'A' ? 'aria-current' : 'aria-pressed';

          if (attr === 'aria-current' && !isActive) el.removeAttribute('aria-current');
          else el.setAttribute(attr, isActive ? 'true' : 'false');
        });
      }

      function restore() {
        if (initialSrc) image.setAttribute('src', initialSrc);
        if (initialSrcset) image.setAttribute('srcset', initialSrcset);
        if (initialHref && link) link.setAttribute('href', initialHref);
      }

      group.querySelectorAll('.ek-swatch').forEach(function (button) {
        button.addEventListener('mouseenter', function () {
          apply(button);
        });
        button.addEventListener('focus', function () {
          apply(button);
        });
        button.addEventListener('click', function (event) {
          event.preventDefault();
          apply(button);
          var href = button.getAttribute('data-ek-swatch-url');
          if (href) window.location.href = href;
        });
      });

      group.addEventListener('mouseleave', restore);
    });
  }

  function init() {
    initReveal();
    initCountdown();
    initCardSwatches();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
